import { Box, makeStyles, Typography } from '@material-ui/core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CollapsableCard from 'components/Generic/CollapsableCard';
import { useClientContractsForClientAndBuyer } from 'hooks';
import { ColorTypeEnum, fonts } from 'theme';
import { Buyer, ClientContractsPost, Event } from 'types';
import { notify } from 'components/Notifier';
import { postClientContracts } from 'services/apiOperations';
import { CollapsableContentShow } from './CollapsableContentShow';
import { CollapsableContentUpdate } from './CollapsableContentUpdate';

const useStyles = makeStyles({
  buyerCard: {
    display: 'flex',
    flexDirection: 'row',
    width: '1000px',
  },
  buyerTitle: {
    marginLeft: '14px',
    marginRight: '14px',
    fontFamily: fonts.secondary,
    fontWeight: 600,
  },
});

interface CollapsableContentContainerProps {
  buyer: Buyer;
  clientId: string;
  canUpdate: boolean;
}

export const CollapsableContentContainer: React.FC<CollapsableContentContainerProps> = ({
  buyer,
  clientId,
  canUpdate,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'clients' });
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [clientContracts] = useClientContractsForClientAndBuyer(clientId, buyer.id.toString());
  const clientContractsUpdate = useMemo(() => {
    return clientContracts;
  }, [clientContracts]);

  const onValidateClientContractUpdate = (clientContractsPost: ClientContractsPost) => {
    postClientContracts(clientContractsPost)
      .then(() => {
        notify({
          message: pt('buyersList.update.successMessage'),
          severity: 'success',
        });
        setIsUpdateMode(false);
        PubSub.publish(Event.REFRESH_CLIENT_CONTRACTS);
      })
      .catch(() => {
        notify({
          message: t('errorGeneric'),
          severity: 'error',
        });
      });
  };

  const collapseContent =
    isUpdateMode && canUpdate ? (
      <CollapsableContentUpdate
        clientContracts={clientContractsUpdate}
        buyerId={`${buyer.id}`}
        clientId={clientId}
        onCancel={() => setIsUpdateMode(false)}
        onValidate={onValidateClientContractUpdate}
      />
    ) : (
      <CollapsableContentShow
        clientContracts={clientContracts}
        clientId={clientId}
        buyerId={`${buyer.id}`}
        canUpdate={canUpdate}
        onClickUpdateButton={() => setIsUpdateMode(true)}
      />
    );

  return (
    <CollapsableCard key={buyer['@id']} color={ColorTypeEnum.PRIMARY} collapsableContent={collapseContent}>
      <Box className={classes.buyerCard}>
        <Typography className={classes.buyerTitle}>{buyer.name}</Typography>
      </Box>
    </CollapsableCard>
  );
};
